import React, { useEffect, useState } from "react";
import { PanelLeftOpen, PanelLeftClose } from "lucide-react";
import { useSidebarState } from "../context/SidebarStateContext";

/**
 * SidebarToggleButton — replaces the old "Open in new tab" affordance on
 * embedded-tool pages. Toggles the AppShell sidebar visibility:
 *
 *   • Desktop / tablet (>= 768px): flips `collapsed` (persisted in
 *     localStorage). Collapsed state hides the sidebar rail, giving the
 *     iframe/table the full viewport width.
 *   • Mobile (< 768px): flips `mobileNavOpen` (the drawer).
 *
 * The button label swaps between "Hide Menu" (sidebar is currently
 * visible/open) and "Show Menu" (sidebar is currently collapsed/closed)
 * on every breakpoint.
 */
export default function SidebarToggleButton({
  className = "",
  testId = "sidebar-toggle-btn",
}) {
  const { collapsed, toggleCollapsed, mobileNavOpen, setMobileNavOpen } =
    useSidebarState();

  // Track viewport so the button controls the right piece of shell state
  // (drawer vs rail) at every breakpoint. Updated on resize so an
  // orientation flip or window resize keeps the label truthful.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const sidebarVisible = isMobile ? mobileNavOpen : !collapsed;
  const label = sidebarVisible ? "Hide Menu" : "Show Menu";
  const Icon = sidebarVisible ? PanelLeftClose : PanelLeftOpen;

  const onClick = () => {
    if (isMobile) setMobileNavOpen((v) => !v);
    else toggleCollapsed();
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={sidebarVisible}
      aria-label={label}
      className={
        "inline-flex items-center gap-2 rounded-full border border-primary/40 bg-white " +
        "px-3 py-1.5 text-xs md:text-sm font-semibold text-primary shadow-sm " +
        "hover:bg-primary hover:text-white hover:border-primary hover:shadow " +
        "focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors " +
        className
      }
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}
